import { Job } from "../models/Job";
import { getAuthHeaders } from "../utils/authHeaders";
import { API_URL } from "../config/api";

export interface MatchedJob {
  job: Job;
  matchScore: number;
  matchReasons: string[];
  computedAt: string;
}

export interface ProfileCompleteness {
  hasSpecialization: boolean;
  hasProfession: boolean;
  hasAccreditation: boolean;
  hasResearchInterests: boolean;
  suggestions: string[];
}

export interface RecommendationsResponse {
  recommendations: MatchedJob[];
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
    limit: number;
  };
  profileCompleteness: ProfileCompleteness;
}

export const fetchRecommendations = async (
  page: number = 1,
  limit: number = 10
): Promise<RecommendationsResponse> => {
  const response = await fetch(
    `${API_URL}/matching/recommendations?page=${page}&limit=${limit}`,
    {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch recommendations");

  const data = await response.json();

  // The endpoint returns populated Mongo job documents, which carry _id and no
  // id. Map backend _id to frontend id the same way jobService does, so callers
  // can link to /jobs/:id straight off the Job model.
  return {
    ...data,
    recommendations: (data.recommendations || []).map((rec: any) => ({
      ...rec,
      job: { ...rec.job, id: rec.job?._id || rec.job?.id },
    })),
  };
};

export const fetchJobMatchScore = async (
  jobId: string
): Promise<{ matchScore: number; matchReasons: string[] }> => {
  const response = await fetch(`${API_URL}/matching/score/${jobId}`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
  });
  if (!response.ok) throw new Error("Failed to fetch match score");
  return response.json();
};
